import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserCog, ArrowLeft } from "lucide-react";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function CoordinatorInvite() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const { refresh } = useAuth();
    const code = params.get("code") || "";
    const [invite, setInvite] = useState(null);
    const [form, setForm] = useState({ password: "", full_name: "", phone: "" });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (!code) { setErr("Missing invite code"); return; }
        (async () => {
            try {
                const { data } = await api.get(`/auth/coordinator-invite/${code}`);
                setInvite(data);
                setForm((f) => ({ ...f, full_name: data.full_name || "" }));
            } catch (e) { setErr(e?.response?.data?.detail || "Invalid invite"); }
        })();
    }, [code]);

    async function submit(e) {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/auth/register/coordinator", {
                email: invite.email,
                password: form.password,
                full_name: form.full_name,
                phone: form.phone || undefined,
                invite_code: code,
            });
            setToken(data.access_token);
            await refresh();
            toast.success("Welcome, coordinator");
            nav("/dashboard");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally { setBusy(false); }
    }

    return (
        <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-md rounded-3xl border-stone-200">
                <CardContent className="p-8">
                    <Link to="/" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-800 mb-6">
                        <ArrowLeft className="w-4 h-4" /> Home
                    </Link>
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center mb-4">
                        <UserCog className="w-5 h-5 text-emerald-700" />
                    </div>
                    <h1 className="font-outfit text-2xl font-semibold text-stone-900">Accept your coordinator invite</h1>
                    {err && <p className="mt-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">{err}</p>}
                    {invite && (
                        <>
                            <p className="text-sm text-stone-500 font-dmsans mt-1">Invited: <strong>{invite.email}</strong></p>
                            <form onSubmit={submit} className="space-y-4 mt-6">
                                <div>
                                    <Label>Full name</Label>
                                    <Input data-testid="coord-invite-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required className="mt-1.5 h-12 rounded-xl" />
                                </div>
                                <div>
                                    <Label>Phone <span className="text-xs text-stone-400">(optional)</span></Label>
                                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 h-12 rounded-xl" />
                                </div>
                                <div>
                                    <Label>Password</Label>
                                    <Input data-testid="coord-invite-password" type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="mt-1.5 h-12 rounded-xl" />
                                </div>
                                <Button data-testid="coord-invite-submit" type="submit" disabled={busy} className="w-full h-12 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white">
                                    {busy ? "…" : "Create coordinator account"}
                                </Button>
                            </form>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
